import React, { useEffect, useState } from "react";
import {
  Badge,
  Body1,
  Button,
  Card,
  CardHeader,
  Caption1,
  Divider,
  Spinner,
  Subtitle2,
  makeStyles,
  tokens
} from "@fluentui/react-components";
import { t } from "i18next";
import { useAuthHelper } from "../contexts/AuthHelper";
import { useSubscription } from "../contexts/Subscription";
import { Localization } from "../locales/schema";
import { Subscription } from "../model/model";
import { ContactPublisherCallout } from "./common/ContactPublisherCallout";
import { EmptyState } from "./common/EmptyState";
import { StatusMessage } from "./common/StatusMessage";

const useStyles = makeStyles({
  root: {
    display: "grid",
    gap: tokens.spacingVerticalXL,
  },
  headerBlock: {
    display: "grid",
    gap: tokens.spacingVerticalS,
  },
  details: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: tokens.spacingVerticalM,
  },
  detailItem: {
    display: "grid",
    gap: tokens.spacingVerticalXS,
    padding: tokens.spacingVerticalM,
    borderRadius: tokens.borderRadiusLarge,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  actionRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: tokens.spacingHorizontalM,
    alignItems: "center",
  },
});

interface IActivationProps {
  marketplaceToken: string | null;
  onActivationSucceeded: () => void;
}

export const Activaton: React.FC<IActivationProps> = ({ marketplaceToken, onActivationSucceeded }) => {
  const subscription = useSubscription();
  const auth = useAuthHelper();
  const styles = useStyles();

  const [loading, setLoading] = useState<boolean>(false);
  const [subscr, setSubscr] = useState<Subscription | undefined>(undefined);
  const [err, setErr] = useState<string | undefined>(undefined);
  const [activateErr, setActivateErr] = useState<string | undefined>(undefined);
  const [completed, setCompleted] = useState<boolean>(false);

  const resolve = async (token: string, accessToken: string): Promise<void> => {
    setLoading(true);
    setErr(undefined);
    try {
      const nextSubscription = await subscription.resolveSubsription(token, accessToken);
      setSubscr(nextSubscription);
    }
    catch (error) {
      setSubscr(undefined);
      setErr(error instanceof Error ? error.message : String(error));
    }
    finally {
      setLoading(false);
    }
  };

  const activate = async (accessToken: string): Promise<void> => {
    if (!subscr) {
      return;
    }

    setLoading(true);
    setActivateErr(undefined);
    try {
      await subscription.activateSubscription(
        subscr.subscription.beneficiary.tenantId,
        subscr.id,
        subscr.planId,
        subscr.subscription.isFreeTrial,
        subscr.quantity,
        accessToken
      );
      setCompleted(true);
      onActivationSucceeded();
    }
    catch (error) {
      setActivateErr(error instanceof Error ? error.message : String(error));
    }
    finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!marketplaceToken) {
      return;
    }

    auth.getAccessToken().then(async (token) => {
      await resolve(marketplaceToken, token ?? "");
    });
  }, [marketplaceToken]);

  if (!marketplaceToken) {
    return (
      <EmptyState
        title="Marketplace token is missing"
        description="Open this page from Microsoft Marketplace so the activation flow can resolve the purchased subscription."
      />
    );
  }

  if (loading && !subscr) {
    return (
      <Card>
        <CardHeader
          header={<Subtitle2>Preparing activation</Subtitle2>}
          description={<Caption1>Resolving the Microsoft Marketplace subscription details.</Caption1>}
        />
        <Spinner label="Loading subscription details" />
      </Card>
    );
  }

  if (!subscr) {
    return (
      <Card>
        <CardHeader
          header={<Subtitle2>Subscription activation</Subtitle2>}
          description={<Caption1>We could not resolve the marketplace subscription.</Caption1>}
        />
        <EmptyState
          title="Subscription info not found"
          description="Make sure the page was opened from AppSource or Microsoft Marketplace."
        />
        {err && (
          <>
            <StatusMessage intent="error" title="Resolve error">
              {err}
            </StatusMessage>
            <div className={styles.actionRow}>
              <Button appearance="secondary" onClick={() => onActivationSucceeded()}>
                Open tenant management
              </Button>
            </div>
            <ContactPublisherCallout />
          </>
        )}
      </Card>
    );
  }

  const canActivate = subscr.subscription.saasSubscriptionStatus === "PendingFulfillmentStart";

  return (
    <Card>
      <div className={styles.root}>
        <div className={styles.headerBlock}>
          <CardHeader
            header={<Subtitle2>Activate your Marketplace subscription</Subtitle2>}
            description={
              <Caption1>
                Review the purchase details returned by Microsoft Marketplace before provisioning the tenant.
              </Caption1>
            }
          />
          <div className={styles.actionRow}>
            <Badge appearance="filled" color={canActivate ? "warning" : "success"}>
              {subscr.subscription.saasSubscriptionStatus}
            </Badge>
            <Caption1>Tenant {subscr.subscription.beneficiary.tenantId}</Caption1>
          </div>
        </div>

        <div className={styles.details}>
          <div className={styles.detailItem}>
            <Caption1>{t(Localization.Subscription.Id)}</Caption1>
            <Body1>{subscr.subscription.id}</Body1>
          </div>
          <div className={styles.detailItem}>
            <Caption1>{t(Localization.Subscription.NameTitle)}</Caption1>
            <Body1>{subscr.subscription.name}</Body1>
          </div>
          <div className={styles.detailItem}>
            <Caption1>{t(Localization.Subscription.StatusTitle)}</Caption1>
            <Body1>{subscr.subscription.saasSubscriptionStatus}</Body1>
          </div>
          <div className={styles.detailItem}>
            <Caption1>{t(Localization.Subscription.RequestedByTitle)}</Caption1>
            <Body1>{subscr.subscription.beneficiary.emailId}</Body1>
          </div>
          <div className={styles.detailItem}>
            <Caption1>{t(Localization.Subscription.TenantIdTitle)}</Caption1>
            <Body1>{subscr.subscription.beneficiary.tenantId}</Body1>
          </div>
          <div className={styles.detailItem}>
            <Caption1>{t(Localization.Subscription.PlanId)}</Caption1>
            <Body1>{subscr.subscription.planId}</Body1>
          </div>
        </div>

        <Divider />

        {completed && (
          <StatusMessage intent="success" title="Activation completed">
            {t(Localization.Subscription.IsAlreadyActivatedTitle)}
          </StatusMessage>
        )}

        {activateErr && (
          <>
            <StatusMessage intent="error" title="Activation failed">
              {activateErr}
            </StatusMessage>
            <ContactPublisherCallout />
          </>
        )}

        <div className={styles.actionRow}>
          {canActivate ? (
            <Button
              appearance="primary"
              disabled={loading}
              onClick={async () => {
                try {
                  await activate(await auth.getAccessToken() ?? "");
                }
                catch (error) {
                  setErr(error instanceof Error ? error.message : String(error));
                }
              }}
            >
              {loading ? "Activating..." : "Activate subscription"}
            </Button>
          ) : (
            <Body1>Your subscription is already configured. Open tenant management to continue.</Body1>
          )}

          <Button appearance="secondary" onClick={() => onActivationSucceeded()}>
            Open tenant management
          </Button>
        </div>

        {err && (
          <>
            <StatusMessage intent="error" title="Access token or activation error">
              {err}
            </StatusMessage>
            <div className={styles.actionRow}>
              <Button appearance="secondary" onClick={() => onActivationSucceeded()}>
                Open tenant management
              </Button>
            </div>
            <ContactPublisherCallout />
          </>
        )}
      </div>
    </Card>
  );
};
